import { supabase } from '../lib/supabase';

export interface SubscriptionPlan {
  id: string;
  plan_name: string;
  plan_type: 'free' | 'premium' | 'enterprise';
  price_vatu: number;
  max_group_members: number | null;
  max_contributions_per_month: number | null;
  features: {
    reporting: string;
    support: string;
    storage: string;
    features: string[];
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  subscription_status: 'active' | 'expired' | 'pending' | 'locked';
  start_date: string | null;
  end_date: string | null;
  payment_due_date: string | null;
  last_payment_date: string | null;
  android_sync_date: string | null;
  access_locked: boolean;
  locked_reason: string | null;
  created_at: string;
  updated_at: string;
  plan?: SubscriptionPlan;
}

export interface PaymentTransaction {
  id: string;
  subscription_id: string;
  user_id: string;
  amount_vatu: number;
  payment_method: 'bank_transfer';
  payment_proof_url: string | null;
  payment_reference: string | null;
  payment_status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  subscription?: UserSubscription;
}

class SubscriptionService {
  async getPlans(): Promise<SubscriptionPlan[]> {
    const { data, error } = await supabase
      .from('subscription_plans')
      .select('*')
      .eq('is_active', true)
      .order('price_vatu', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          id,
          user_id,
          plan_id,
          subscription_status,
          start_date,
          end_date,
          payment_due_date,
          last_payment_date,
          android_sync_date,
          access_locked,
          locked_reason,
          created_at,
          updated_at,
          plan:subscription_plans(
            id,
            plan_name,
            plan_type,
            price_vatu,
            billing_model,
            price_per_member_vatu,
            max_group_members,
            max_contributions_per_month,
            features,
            is_active,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      // Cache subscription data for offline use
      if (data) {
        this.cacheSubscriptionStatus(data);
      }
      
      return data;
    } catch (e) {
      console.log('Online subscription fetch failed, checking cache:', e);
      // Return cached data if available
      const cached = this.getCachedSubscriptionStatus();
      return cached;
    }
  }

  async createSubscription(userId: string, planId: string): Promise<UserSubscription> {
    const { data, error } = await supabase
      .from('user_subscriptions')
      .insert({
        user_id: userId,
        plan_id: planId,
        subscription_status: 'pending',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async submitPayment(
    subscriptionId: string,
    userId: string,
    amountVatu: number,
    paymentProofUrl: string | null,
    paymentReference: string
  ): Promise<PaymentTransaction> {
    const { data, error } = await supabase
      .from('payment_transactions')
      .insert({
        subscription_id: subscriptionId,
        user_id: userId,
        amount_vatu: amountVatu,
        payment_method: 'bank_transfer',
        payment_proof_url: paymentProofUrl,
        payment_reference: paymentReference,
        payment_status: 'pending',
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async getPendingPayments(): Promise<PaymentTransaction[]> {
    const { data, error } = await supabase
      .from('payment_transactions')
      .select(`
        id,
        subscription_id,
        user_id,
        amount_vatu,
        payment_method,
        payment_proof_url,
        payment_reference,
        payment_status,
        submitted_at,
        approved_by,
        approved_at,
        rejection_reason,
        admin_notes,
        created_at,
        updated_at,
        subscription:user_subscriptions(
          id,
          user_id,
          plan_id,
          subscription_status,
          start_date,
          end_date,
          plan:subscription_plans(
            id,
            plan_name,
            plan_type,
            price_vatu
          )
        )
      `)
      .eq('payment_status', 'pending')
      .order('submitted_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async getPaymentHistory(userId?: string): Promise<PaymentTransaction[]> {
    let query = supabase
      .from('payment_transactions')
      .select(`
        id,
        subscription_id,
        user_id,
        amount_vatu,
        payment_method,
        payment_proof_url,
        payment_reference,
        payment_status,
        submitted_at,
        approved_by,
        approved_at,
        rejection_reason,
        admin_notes,
        created_at,
        updated_at,
        subscription:user_subscriptions(
          id,
          user_id,
          plan_id,
          subscription_status,
          start_date,
          end_date,
          plan:subscription_plans(
            id,
            plan_name,
            plan_type,
            price_vatu
          )
        )
      `);

    if (userId) {
      query = query.eq('user_id', userId);
    }

    const { data, error } = await query.order('submitted_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async approvePayment(paymentId: string, adminId: string, notes?: string): Promise<any> {
    const { data, error } = await supabase.rpc('approve_payment', {
      payment_id: paymentId,
      admin_id: adminId,
      notes: notes || null,
    });

    if (error) throw error;
    return data;
  }

  async rejectPayment(paymentId: string, adminId: string, reason: string): Promise<any> {
    const { data, error } = await supabase.rpc('reject_payment', {
      payment_id: paymentId,
      admin_id: adminId,
      reason: reason,
    });

    if (error) throw error;
    return data;
  }

  async checkSubscriptionStatus(userId: string): Promise<{
    hasAccess: boolean;
    isLocked: boolean;
    reason: string | null;
  }> {
    const subscription = await this.getUserSubscription(userId);

    if (!subscription) {
      return { hasAccess: false, isLocked: false, reason: 'No active subscription' };
    }

    return {
      hasAccess: subscription.subscription_status === 'active' && !subscription.access_locked,
      isLocked: subscription.access_locked,
      reason: subscription.locked_reason,
    };
  }

  // Cache subscription status for offline use
  private cacheSubscriptionStatus(subscription: UserSubscription | null): void {
    try {
      if (subscription === null) {
        localStorage.removeItem('cached_subscription_status');
        localStorage.removeItem('cached_subscription_end_date');
        localStorage.removeItem('cached_subscription_access_status');
        localStorage.removeItem('cached_subscription_access_locked');
        return;
      }

      localStorage.setItem('cached_subscription_status', JSON.stringify(subscription));
      localStorage.setItem('cached_subscription_end_date', subscription.end_date || '');
      localStorage.setItem('cached_subscription_access_status', subscription.subscription_status || '');
      localStorage.setItem('cached_subscription_access_locked', String(subscription.access_locked || false));
    } catch (e) {
      console.error('Error caching subscription status:', e);
    }
  }

  // Get cached subscription status
  private getCachedSubscriptionStatus(): UserSubscription | null {
    try {
      const endDate = localStorage.getItem('cached_subscription_end_date');
      const accessStatus = localStorage.getItem('cached_subscription_access_status');
      const accessLocked = localStorage.getItem('cached_subscription_access_locked') === 'true';
      const cached = localStorage.getItem('cached_subscription_status');

      if (cached) {
        return JSON.parse(cached) as UserSubscription;
      }

      if (endDate && endDate !== '') {
        return {
          id: '',
          user_id: '',
          plan_id: '',
          subscription_status: (accessStatus as any) || 'locked',
          end_date: endDate,
          access_locked: accessLocked,
        } as UserSubscription;
      }

      return null;
    } catch (e) {
      console.error('Error getting cached subscription status:', e);
      return null;
    }
  }

  // Check if subscription is expired (works offline)
  async isSubscriptionExpired(userId: string, useOfflineCache: boolean = true): Promise<boolean> {
    try {
      // First check offline cache if available
      if (useOfflineCache) {
        const cached = this.getCachedSubscriptionStatus();
        if (cached) {
          // Check if locked or status is not active
          if (cached.access_locked || cached.subscription_status !== 'active') {
            return true;
          }
          
          // For pay-as-you-go plans, end_date is NULL, so only check status
          // For other plans, check end_date
          const isPayAsYouGo = cached.plan?.billing_model === 'per_member' || 
                               (cached.plan?.plan_name || '').toLowerCase().includes('pay-as-you-go') ||
                               (cached.plan?.plan_name || '').toLowerCase().includes('pay as you go');
          
          if (!isPayAsYouGo) {
            const endDate = cached.end_date ? new Date(cached.end_date) : null;
            const now = new Date();
            
            if (endDate && now > endDate) {
              return true;
            }
          }
        }
      }

      // Try to fetch from server
      try {
        const subscription = await this.getUserSubscription(userId);
        
        // Cache the result
        this.cacheSubscriptionStatus(subscription);

        if (!subscription) {
          return true;
        }

        // Check if locked or status is not active
        if (subscription.access_locked || subscription.subscription_status !== 'active') {
          return true;
        }

        // Check if it's a pay-as-you-go plan
        const plan = subscription.plan;
        const isPayAsYouGo = plan?.billing_model === 'per_member' || 
                             (plan?.plan_name || '').toLowerCase().includes('pay-as-you-go') ||
                             (plan?.plan_name || '').toLowerCase().includes('pay as you go');

        // For pay-as-you-go plans, end_date is NULL, so only check status (already checked above)
        // For other plans, check end_date
        if (!isPayAsYouGo) {
          const endDate = subscription.end_date ? new Date(subscription.end_date) : null;
          const now = new Date();

          if (endDate && now > endDate) {
            return true;
          }
        }

        return false;
      } catch (e) {
        // If online fetch fails, use cached data
        console.log('Online subscription check failed, using cache:', e);
        const cached = this.getCachedSubscriptionStatus();
        if (cached) {
          // Check if locked or status is not active
          if (cached.access_locked || cached.subscription_status !== 'active') {
            return true;
          }
          
          // For pay-as-you-go plans, end_date is NULL, so only check status
          const isPayAsYouGo = cached.plan?.billing_model === 'per_member' || 
                               (cached.plan?.plan_name || '').toLowerCase().includes('pay-as-you-go') ||
                               (cached.plan?.plan_name || '').toLowerCase().includes('pay as you go');
          
          if (!isPayAsYouGo) {
            const endDate = cached.end_date ? new Date(cached.end_date) : null;
            const now = new Date();
            
            if (endDate && now > endDate) {
              return true;
            }
          }
        }
        return false;
      }
    } catch (e) {
      console.error('Error checking subscription expiry:', e);
      // On error, check cache as fallback
      const cached = this.getCachedSubscriptionStatus();
      if (cached) {
        const endDate = cached.end_date ? new Date(cached.end_date) : null;
        const now = new Date();
        
        if (endDate && now > endDate) {
          return true;
        }
        
        if (cached.access_locked || cached.subscription_status !== 'active') {
          return true;
        }
      }
      return false;
    }
  }

  // Get subscription expiry message
  async getSubscriptionExpiryMessage(userId: string): Promise<string> {
    const cached = this.getCachedSubscriptionStatus();
    if (cached && cached.end_date) {
      const endDate = new Date(cached.end_date);
      const now = new Date();
      if (now > endDate) {
        return `Your subscription expired on ${endDate.toLocaleDateString()}. Please renew to continue using the platform.`;
      }
    }

    try {
      const subscription = await this.getUserSubscription(userId);
      if (subscription && subscription.end_date) {
        const endDate = new Date(subscription.end_date);
        const now = new Date();
        if (now > endDate) {
          return `Your subscription expired on ${endDate.toLocaleDateString()}. Please renew to continue using the platform.`;
        }
      }
    } catch (e) {
      console.error('Error getting subscription expiry message:', e);
    }

    return 'Your subscription has expired. Please renew to continue using the platform.';
  }

  // Refresh subscription cache (call on app start)
  async refreshSubscriptionCache(userId: string): Promise<void> {
    try {
      const subscription = await this.getUserSubscription(userId);
      this.cacheSubscriptionStatus(subscription);
      console.log('Subscription cache refreshed');
    } catch (e) {
      console.error('Error refreshing subscription cache:', e);
    }
  }
}

export const subscriptionService = new SubscriptionService();